/**
 * Row-level updates of the `['generations', …]` query cache.
 *
 * The playground history is an infinite query, and refetching it re-requests
 * EVERY loaded page, one after another, so the new card of a create or a
 * status change would wait for all of them. Instead:
 * - a create fetches its one new row and puts it on top (showCreatedGeneration);
 * - a WebSocket status push re-fetches the one row it concerns
 *   (refreshAfterStatusPush);
 * - the safety-net poll merges its first-page fetch (mergeFreshGenerations).
 * Single-row fetches (GET /inference/:id) only ever concern the viewer's own
 * rows: they 404 for a teammate's row (dashboard sessions carry no team).
 */
import type { InfiniteData, QueryClient, QueryKey } from '@tanstack/react-query';

import {
  generationService,
  mapCachedGenerations,
  type Generation,
  type GenerationRowsCache,
  type GenerationsRange,
  type PaginatedGenerations,
} from './generation-service';

/** Key prefix of the playground history queries (`historyInfiniteQueryOptions`). */
export const GENERATIONS_HISTORY_KEY = ['generations', 'history'] as const;

const rowsOf = (data: GenerationRowsCache<Generation> | undefined): Generation[] => {
  if (!data) return [];
  if ('pages' in data) return data.pages.flatMap((p) => p.data ?? []);
  return Array.isArray(data.data) ? data.data : [];
};

/**
 * The row with this int id in a history query that is on screen now (has an
 * observer), or undefined. Inactive caches don't count: nothing needs fetching
 * for them, and they refetch on their own when they mount again.
 */
export function findActiveHistoryRow(queryClient: QueryClient, id: number): Generation | undefined {
  for (const query of queryClient.getQueryCache().findAll({ queryKey: GENERATIONS_HISTORY_KEY, type: 'active' })) {
    const hit = rowsOf(query.state.data as GenerationRowsCache<Generation> | undefined).find(
      // String-compare ids defensively (legacy rows read .id as an int-string on some paths).
      (g) => String(g.id) === String(id),
    );
    if (hit) return hit;
  }
  return undefined;
}

const RUNNING = new Set<Generation['status']>(['pending', 'processing']);

/**
 * Merges freshly fetched rows into every `['generations']` cache entry, only
 * where a row's status moved forward:
 * - same-status rows are left alone. Partial outputs live only in the cache
 *   (streamed over the WebSocket; the server stores none until completion),
 *   so overwriting a still-running row would blank its streamed previews; a
 *   row that moves from pending to processing keeps the previews it has.
 * - a terminal row (completed, failed, cancelled) never goes back to running:
 *   a response sent before the row finished can arrive after a newer one.
 */
export function mergeFreshGenerations(queryClient: QueryClient, freshRows: ReadonlyArray<Generation | null | undefined>): void {
  const byId = new Map<string, Generation>();
  for (const fresh of freshRows) {
    if (fresh && fresh.id != null && fresh.status) byId.set(String(fresh.id), fresh);
  }
  if (byId.size === 0) return;
  queryClient.setQueriesData<GenerationRowsCache<Generation>>({ queryKey: ['generations'] }, (old) =>
    mapCachedGenerations<Generation>(old, (rows) => {
      let changed = false;
      const data = rows.map((g) => {
        const fresh = byId.get(String(g.id));
        if (!fresh || g.status === fresh.status) return g;
        if (RUNNING.has(fresh.status) && !RUNNING.has(g.status)) return g;
        changed = true;
        return RUNNING.has(fresh.status) && !fresh.output_urls?.length && g.output_urls?.length
          ? { ...g, ...fresh, output_urls: g.output_urls }
          : { ...g, ...fresh };
      });
      return changed ? data : rows;
    }),
  );
}

/** One-row form of mergeFreshGenerations. */
export function mergeFreshGeneration(queryClient: QueryClient, fresh: Generation | null | undefined): void {
  mergeFreshGenerations(queryClient, [fresh]);
}

/** Per client: aurous ids being fetched → whether another fetch was asked for meanwhile. */
const inFlight = new WeakMap<QueryClient, Map<string, boolean>>();

/**
 * Fetches one generation by its opaque id (an int id is a guaranteed 404 on
 * GET /inference/:id) and merges it. Best-effort, never throws.
 *
 * One request per id at a time. `again: true` (WebSocket pushes) fetches once
 * more after a running request, because that request may predate the change
 * being pushed; the poll passes false and simply skips a tick.
 */
export async function refreshGeneration(
  queryClient: QueryClient,
  aurousId: string,
  opts: { again?: boolean } = {},
): Promise<void> {
  let pending = inFlight.get(queryClient);
  if (!pending) {
    pending = new Map();
    inFlight.set(queryClient, pending);
  }
  if (pending.has(aurousId)) {
    if (opts.again) pending.set(aurousId, true);
    return;
  }
  pending.set(aurousId, false);
  try {
    do {
      pending.set(aurousId, false);
      try {
        mergeFreshGeneration(queryClient, await generationService.getById(aurousId));
      } catch {
        /* best-effort: the poll and later pushes still cover */
      }
    } while (pending.get(aurousId));
  } finally {
    pending.delete(aurousId);
  }
}

/** Per client: rows a create on this client is fetching to show (int id → a push arrived meanwhile). */
const justCreated = new WeakMap<QueryClient, Map<string, boolean>>();

const createdFor = (queryClient: QueryClient): Map<string, boolean> => {
  let rows = justCreated.get(queryClient);
  if (!rows) {
    rows = new Map();
    justCreated.set(queryClient, rows);
  }
  return rows;
};

/** True when a history query with this key lists rows created at `createdAt` (Recent, or a day that covers it). */
function historyCovers(queryKey: QueryKey, createdAt: string | undefined): boolean {
  const range = (queryKey[2] as { range?: GenerationsRange | null } | undefined)?.range ?? null;
  if (!range) return true;
  const t = createdAt ? Date.parse(createdAt) : NaN;
  return t >= Date.parse(range.start) && t < Date.parse(range.end);
}

/**
 * Puts a new row on top of every cached history that lists it (Recent, and a
 * picked day that covers its created_at), and counts it in that history's
 * total. A history that already holds the row, or has not loaded, is left as
 * it is. The next "Load more" overlaps the shifted offset by a row, which the
 * page de-duplicates.
 */
export function insertIntoHistory(queryClient: QueryClient, fresh: Generation): void {
  for (const query of queryClient.getQueryCache().findAll({ queryKey: GENERATIONS_HISTORY_KEY })) {
    if (!historyCovers(query.queryKey, fresh.created_at)) continue;
    queryClient.setQueryData<InfiniteData<PaginatedGenerations>>(query.queryKey, (old) => {
      if (!old?.pages?.length) return old;
      if (old.pages.some((p) => p.data.some((g) => String(g.id) === String(fresh.id)))) return old;
      const [first, ...rest] = old.pages;
      return { ...old, pages: [{ ...first, data: [fresh, ...first.data], total: first.total + 1 }, ...rest] };
    });
  }
}

/** The create response's ids (`{ data }` of POST /inference and POST /inference/video). */
export interface CreatedGeneration {
  id?: string | number;
  aurous_id?: string;
}

/**
 * After a successful create: fetch the new row once (by its opaque id — an
 * int id is a guaranteed 404) and show it on top, instead of refetching every
 * loaded history page first. The fetch also lands a dispatch that already
 * failed by the time the POST returned, a failure whose WebSocket push can be
 * lost to the socket/subscription race. On any error, or without an opaque
 * id, the history is refetched as before. Never throws.
 */
export async function showCreatedGeneration(
  queryClient: QueryClient,
  created: CreatedGeneration | null | undefined,
): Promise<void> {
  // Small single-page entries (getting-started checklist, the composer's Recent read).
  void queryClient.invalidateQueries({
    queryKey: ['generations'],
    predicate: (q) => q.queryKey[1] !== GENERATIONS_HISTORY_KEY[1],
  });
  const aurousId = created?.aurous_id;
  if (!aurousId) {
    void queryClient.invalidateQueries({ queryKey: GENERATIONS_HISTORY_KEY });
    return;
  }
  const rows = createdFor(queryClient);
  const key = created?.id != null ? String(created.id) : null;
  if (key) rows.set(key, false);
  let pushed = false;
  try {
    const fresh = await generationService.getById(aurousId);
    if (!fresh || fresh.id == null || !fresh.status) throw new Error('no row');
    insertIntoHistory(queryClient, fresh);
    mergeFreshGenerations(queryClient, [fresh]);
  } catch {
    void queryClient.invalidateQueries({ queryKey: GENERATIONS_HISTORY_KEY });
  } finally {
    if (key) {
      pushed = rows.get(key) === true;
      rows.delete(key);
    }
  }
  // A status push arrived while the row was being fetched; that fetch may predate it.
  if (pushed) await refreshGeneration(queryClient, aurousId, { again: true });
}

const TERMINAL = new Set(['completed', 'failed', 'cancelled']);

/**
 * After a WebSocket status push for generation `id`:
 * - a row an on-screen history already shows is re-fetched alone, and only
 *   when its cached status differs from the pushed one;
 * - a row a create on this client is still fetching is re-fetched once that
 *   fetch lands (showCreatedGeneration);
 * - any other row (created elsewhere, or a legacy row without an opaque id)
 *   refetches the history so it appears, without restarting a refetch
 *   already running, which will include it.
 * Small single-page entries (getting-started checklist, the composer's Recent
 * read) are refetched only for a finished render.
 */
export function refreshAfterStatusPush(queryClient: QueryClient, id: number, status: string): void {
  if (TERMINAL.has(status)) {
    void queryClient.invalidateQueries({
      queryKey: ['generations'],
      predicate: (q) => q.queryKey[1] !== GENERATIONS_HISTORY_KEY[1],
    });
  }
  const row = findActiveHistoryRow(queryClient, id);
  if (row?.aurous_id) {
    if (row.status !== status) void refreshGeneration(queryClient, row.aurous_id, { again: true });
    return;
  }
  const created = justCreated.get(queryClient);
  if (created?.has(String(id))) {
    created.set(String(id), true);
    return;
  }
  void queryClient.invalidateQueries({ queryKey: GENERATIONS_HISTORY_KEY }, { cancelRefetch: false });
}
